/**
 * Drill Stage Pack
 *
 * Creates Drill (power tool) meshes with physics and theme integration.
 * Loads drill model from .glb file at runtime with skeletal animations.
 * Supports multiple GLB variants via glbKey config.
 */

import { Mesh, Vector3 } from "@babylonjs/core";
import {
  DrillManager,
  EventBus,
  type StageConfigManager,
  type StagePack,
  type StageThemeInfo,
} from "@tony/nl-babylon";
import type { Theme } from "@/context/ThemeContext";

/**
 * Default drill configuration
 */
const DEFAULT_DRILL_CONFIG = {
  count: 1,
  scale: 30, // GLB is ~0.25m real size, scale up for visibility
};

/**
 * GLB-specific configuration for drill variants
 */
interface DrillGlbConfig {
  /** GLB filename (optional - uses default if not specified) */
  glbFile?: string;
  /** GLB path (optional - uses default if not specified) */
  glbPath?: string;
  /** Physics box extents (half-sizes) */
  extents: { x: number; y: number; z: number };
  /** Physics box center offset */
  center: { x: number; y: number; z: number };
  /** Model Y offset for positioning (bottom of model in local coords) */
  modelYOffset: number;
}

/**
 * GLB configurations for each drill variant
 * Keyed by glbKey from route query param
 */
export const DRILL_GLB_CONFIGS: Record<string, DrillGlbConfig> = {
  default: {
    center: { x: 0.188, y: 0.219, z: -0.037 },
    // Uses DrillManager's default GLB
    extents: { x: 0.25, y: 0.11, z: 0.1 },
    modelYOffset: 0.166,
  },
  "drill-006": {
    center: { x: 0.188, y: 0.219, z: -0.037 },
    extents: { x: 0.25, y: 0.11, z: 0.1 },
    glbFile: "Drill-006.glb",
    glbPath: "/3d/original/drill-6/",
    modelYOffset: 0.166,
  },
  "drill-final": {
    center: { x: 0.03, y: -0.02, z: 0.0 },
    extents: { x: 0.126, y: 0.057, z: 0.048 },
    glbFile: "Drill-Final.glb",
    glbPath: "/3d/original/drill-final/",
    modelYOffset: -0.055,
  },
};

/** Valid GLB keys for drill variants */
export type DrillGlbKey = keyof typeof DRILL_GLB_CONFIGS;

/** Default GLB key when none specified */
export const DEFAULT_DRILL_GLB_KEY: DrillGlbKey = "default";

/**
 * Drill stage pack definition
 */
/** Theme-update manager keyed by the meshes array returned from
    createAssets. applyTheme falls back to a temporary manager when called
    with a different array (e.g. the combined stage array after insertPack). */
const drillManagers = new WeakMap<Mesh[], DrillManager>();

export const drillStagePack: StagePack = {
  applyTheme: (meshes, theme: StageThemeInfo) => {
    // Get the stored drillManager
    const drillManager = drillManagers.get(meshes);
    if (!drillManager) {
      // Fallback: create a temporary manager for theme application
      const eventBus = new EventBus();
      const tempManager = new DrillManager(eventBus);
      tempManager.applyThemeColors(
        meshes,
        theme.themeId as Theme,
        theme.isDarkMode,
      );
      return;
    }

    // Collect all drill meshes including children for theme application
    const allMeshes: Mesh[] = [];
    for (const rootMesh of meshes) {
      allMeshes.push(rootMesh);
      for (const child of rootMesh.getChildMeshes()) {
        if (child instanceof Mesh) {
          allMeshes.push(child);
        }
      }
    }
    drillManager.applyThemeColors(
      allMeshes,
      theme.themeId as Theme,
      theme.isDarkMode,
    );
  },

  createAssets: async ({ physicsManager, scene }) => {
    // Read config from StageConfigManager (set by StageContext) or use defaults
    const stageConfigManager = (
      scene.metadata as { stageConfigManager?: StageConfigManager } | undefined
    )?.stageConfigManager;
    const stageConfig = stageConfigManager?.config;
    const config = {
      count: stageConfig?.count ?? DEFAULT_DRILL_CONFIG.count,
      scale: stageConfig?.scale ?? DEFAULT_DRILL_CONFIG.scale,
    };

    // Get GLB-specific config based on glbKey from route query param
    const glbKey = stageConfig?.glbKey ?? DEFAULT_DRILL_GLB_KEY;
    const glbConfig =
      DRILL_GLB_CONFIGS[glbKey] ?? DRILL_GLB_CONFIGS[DEFAULT_DRILL_GLB_KEY];

    console.log(
      "[DrillStagePack] Creating with config:",
      config,
      "glbKey:",
      glbKey,
    );

    const eventBus = new EventBus();
    const drillManager = new DrillManager(eventBus);
    await drillManager.initialize();
    drillManager.setScene(scene);

    const meshes: Mesh[] = [];

    for (let i = 0; i < config.count; i++) {
      // Create drill using DrillManager (async - loads from .glb file)
      // Use GLB config to specify custom file/path if needed
      const drillResult = await drillManager.createDrill(scene, {
        glbFile: glbConfig.glbFile,
        glbPath: glbConfig.glbPath,
        scale: config.scale,
      });

      const { rootMesh } = drillResult;

      // Position: center for single drill, circle for multiple
      // Use glbConfig.modelYOffset to compensate for model's bottom position
      const modelYOffset = glbConfig.modelYOffset * config.scale;
      const startY = 4 - modelYOffset + Math.random() * 0.5; // Start ~4 units up, will fall
      let startX = 0;
      let startZ = 0;

      if (config.count > 1) {
        const angle = (i / config.count) * Math.PI * 2;
        const radius = 3 + Math.random() * 2;
        startX = Math.cos(angle) * radius;
        startZ = Math.sin(angle) * radius;
      }

      rootMesh.position.set(startX, startY, startZ);
      rootMesh.rotation.y = Math.random() * Math.PI * 2;

      // Mark root as physics root for InteractionManager
      rootMesh.metadata = { ...rootMesh.metadata, isPhysicsRoot: true };

      // Add physics body - BOX with explicit extents from glbConfig
      const drillExtents = {
        x: glbConfig.extents.x * config.scale,
        y: glbConfig.extents.y * config.scale,
        z: glbConfig.extents.z * config.scale,
      };
      const drillCenter = {
        x: glbConfig.center.x * config.scale,
        y: glbConfig.center.y * config.scale,
        z: glbConfig.center.z * config.scale,
      };
      physicsManager.addPhysicsBody(rootMesh, {
        angularDamping: 0.8,
        center: drillCenter,
        extents: drillExtents,
        friction: 0.7,
        linearDamping: 0.5,
        mass: 5.0, // Heavier metal tool
        restitution: 0.1, // Less bouncy - metal on surface
        shapeType: "BOX",
      });

      meshes.push(rootMesh);
    }

    // Store drillManager reference for theme updates
    drillManagers.set(meshes, drillManager);

    return meshes;
  },
  displayName: "Power Drill",
  id: "drill",

  interactionConfig: {
    hoverEffects: {
      edgeRenderer: false,
      outlineEdges: true,
    },
    selectionEffects: true,
  },

  physicsConfig: {
    bodyOptions: {
      angularDamping: 0.8,
      friction: 0.7,
      linearDamping: 0.5,
      mass: 5.0,
      restitution: 0.1,
      shapeType: "BOX",
    },
    gravity: new Vector3(0, -9.8, 0),
  },
};
